import type { Screen } from "../types";

// Canvas shell (v2-d-*, v2-m-*): coral rail on tablets/desktops, coral header + drawer on phones.

const NAV_ITEMS: Array<{ key: Screen; label: string; icon: string; moduleGate?: string[] }> = [
  { key: "home", label: "Sell", icon: "storefront" },
  { key: "tables", label: "Tables", icon: "table_restaurant", moduleGate: ["restaurant"] },
  { key: "kitchen", label: "Kitchen", icon: "soup_kitchen", moduleGate: ["restaurant"] },
  { key: "bills", label: "Bills", icon: "receipt_long" },
  // Restaurant-only businesses still need Stock for ingredients (InventoryScreen gates each half).
  { key: "inventory", label: "Stock", icon: "inventory_2", moduleGate: ["inventory", "restaurant"] },
  { key: "settings", label: "Settings", icon: "tune" },
];

/** Only what this store can use: an item whose module is off is not shown at all (canvas v2-d-core-sell, 2026-10-16). */
function visibleNavItems(installedModules: Set<string>) {
  return NAV_ITEMS.filter((n) => !n.moduleGate || n.moduleGate.some((m) => installedModules.has(m)));
}

export const NAV_LABEL: Record<Screen, string> = { home: "Sell", tables: "Tables", kitchen: "Kitchen", bills: "Bills", inventory: "Stock", settings: "Settings", messages: "Messages" };

const Icon = ({ name, size = 24, fill }: { name: string; size?: number; fill?: boolean }) => (
  <span className="ms" aria-hidden="true" style={{ fontSize: size, fontVariationSettings: fill ? "'FILL' 1" : undefined }}>
    {name}
  </span>
);

export const initialsOf = (name: string) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export interface NavRailProps {
  screen: Screen;
  installedModules: Set<string>;
  onNavigate: (screen: Screen) => void;
  staffName: string;
  onStaffMenu: () => void;
  notifCount: number;
  onOpenNotif: () => void;
}

// Item height plus the gap between items (styles.css .pos-rail-item / .pos-rail-nav).
const RAIL_STEP = 68;

/** Tablet/desktop rail on the coral background. */
export function NavRail({ screen, installedModules, onNavigate, staffName, onStaffMenu, notifCount, onOpenNotif }: NavRailProps) {
  const items = visibleNavItems(installedModules);
  const activeIndex = items.findIndex((n) => n.key === screen);
  return (
    <div className="pos-rail">
      <div className="pos-rail-logo">C</div>
      <div className="pos-rail-app">POS</div>
      <div className="pos-rail-rule" />
      <nav aria-label="Main" className="pos-rail-nav">
        {/* One stub for the whole rail, so it slides from the old item to the new one. */}
        {activeIndex >= 0 && (
          <span className="pos-rail-stub" aria-hidden="true" style={{ transform: `translateY(${activeIndex * RAIL_STEP}px)` }}>
            <i />
          </span>
        )}
        {items.map((n) => {
          const active = screen === n.key;
          return (
            <button
              key={n.key}
              aria-label={n.label}
              aria-current={active ? "page" : undefined}
              className={`pos-rail-item${active ? " on" : ""}`}
              onClick={() => onNavigate(n.key)}
            >
              <Icon name={n.icon} fill={active} />
              <span className="pos-rail-label">{n.label}</span>
            </button>
          );
        })}
      </nav>
      <button className="pos-rail-bell" aria-label={notifCount ? `Alerts, ${notifCount} new` : "Alerts"} onClick={onOpenNotif}>
        <Icon name="notifications" size={22} />
        {notifCount > 0 && <span className="pos-badge">{notifCount}</span>}
      </button>
      <button className="pos-rail-me" aria-label={`${staffName}, switch staff or sign out`} onClick={onStaffMenu}>
        {initialsOf(staffName)}
        <span className="pos-rail-swap">
          <Icon name="swap_horiz" size={13} />
        </span>
      </button>
    </div>
  );
}

export interface MobileTopBarProps {
  title: string;
  staffName: string;
  onOpenNav: () => void;
  onStaffMenu: () => void;
  notifCount: number;
  onOpenNotif: () => void;
}

/** Phone header on the coral background. */
export function MobileTopBar({ title, staffName, onOpenNav, onStaffMenu, notifCount, onOpenNotif }: MobileTopBarProps) {
  return (
    <header className="pos-mtop">
      <button className="pos-mtop-btn" aria-label="Open navigation" onClick={onOpenNav}>
        <Icon name="menu" />
      </button>
      <h1>{title === "Sell" ? <>CliqSuite <small>POS</small></> : title}</h1>
      <button className="pos-mtop-btn" aria-label={notifCount ? `Alerts, ${notifCount} new` : "Alerts"} onClick={onOpenNotif}>
        <Icon name="notifications" size={23} />
        {notifCount > 0 && <span className="pos-badge">{notifCount}</span>}
      </button>
      <button className="pos-mtop-me" aria-label={`${staffName}, switch staff or sign out`} onClick={onStaffMenu}>
        {initialsOf(staffName)}
      </button>
    </header>
  );
}

export interface NavDrawerProps {
  open: boolean;
  screen: Screen;
  installedModules: Set<string>;
  storeName: string;
  onNavigate: (screen: Screen) => void;
  onClose: () => void;
}

/** Phone slide-in nav drawer. */
export function NavDrawer({ open, screen, installedModules, storeName, onNavigate, onClose }: NavDrawerProps) {
  if (!open) return null;
  return (
    <>
      <div className="pos-scrim" onClick={onClose} />
      <nav aria-label="Main" className="pos-drawer">
        <div className="pos-drawer-head">
          <div className="pos-rail-logo">C</div>
          <div>
            <b>CliqSuite POS</b>
            <small>{storeName}</small>
          </div>
        </div>
        {visibleNavItems(installedModules).map((n) => {
          const active = screen === n.key;
          return (
            <button key={n.key} aria-current={active ? "page" : undefined} className={`pos-drawer-item${active ? " on" : ""}`} onClick={() => onNavigate(n.key)}>
              <Icon name={n.icon} size={22} fill={active} />
              {n.label}
            </button>
          );
        })}
      </nav>
    </>
  );
}
